import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Constants } from 'app/utility/constants';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CacheService } from './cache.service';
import { UserVariableClientType } from 'app/utility/enums';
import { VehicleSearchParamsModel } from 'app/models/dms/vehicleSearchParams.model';
import { ContactSearchParamsModel } from 'app/models/dms/contactSearchParams.model';
import { OpportunitySearchParamsModel } from 'app/models/dms/opportunitySearchParams.model';
import { SaleSearchParamsModel } from 'app/models/dms/saleSearchParams.model';
import { Capacitor } from '@capacitor/core';
import { Platform } from '@ionic/angular';

@Injectable({
    providedIn: 'root'
})
export class ProfileService
{
	private readonly searchParamsCacheMilliseconds: number = 5 * 60 * 1000;
	private readonly rememberSearchParamsCacheMilliseconds: number = 10 * 365 * 24 * 60 * 60 * 1000;


    constructor(
        private httpClient: HttpClient,
        private cache: CacheService,
        private platform: Platform
    ) { }

    getCurrentPlatform(): UserVariableClientType
    {
        if (Capacitor.isNativePlatform())
        {
            return UserVariableClientType.Native;
        }
        else if (localStorage.getItem(Constants.LocalStorage_OverrideClientID))
		{
            return UserVariableClientType.Embedded;
		}

        if (this.platform.width() > 0 && this.platform.width() < 800)
        {
            return UserVariableClientType.SmallScreenPwa;
        }

        return UserVariableClientType.LargeScreenPwa;
    }

    private getRememberSearchParamsCacheKey(): string
    {
        return 'profile-rememberSearchParams';
    }

    private getSearchParamsCacheKey(prefix: string, platform: UserVariableClientType): string
    {
        return `${prefix}-${platform}`;
    }

    private isDifferent(a: any, b: any): boolean
    {
        return JSON.stringify(a) !== JSON.stringify(b);
    }

    private syncCachedValue<T>(cacheKey: string, current: T, next: T, maxAge: number): void
    {
        if (current !== undefined && !this.isDifferent(current, next))
        {
            return;
        }

        this.cache.clear(cacheKey);
        this.cache.set(cacheKey, next, maxAge);
    }

    public clearProfileCache(): void
    {
        this.cache.clear(this.getRememberSearchParamsCacheKey());

        this.clearSearchParamsCache();
    }

    public clearSearchParamsCache(): void
    {

        const platforms: UserVariableClientType[] = [
            UserVariableClientType.Native,
            UserVariableClientType.Embedded,
            UserVariableClientType.SmallScreenPwa,
            UserVariableClientType.LargeScreenPwa
        ];

        for (const platform of platforms)
        {
            this.cache.clear(this.getSearchParamsCacheKey('profile-vehicleSearchParams', platform));
            this.cache.clear(this.getSearchParamsCacheKey('profile-contactSearchParams', platform));
            this.cache.clear(this.getSearchParamsCacheKey('profile-opportunitySearchParams', platform));
            this.cache.clear(this.getSearchParamsCacheKey('profile-saleSearchParams', platform));
        }
    }

    checkRememberSearchParams(): Observable<boolean>
    {
        const cacheKey = this.getRememberSearchParamsCacheKey();
        return this.cache.get(
            cacheKey,
            this.httpClient.get<boolean>(Constants.ApiUrl + 'Profiles/CheckRememberSearchParams'),
            this.rememberSearchParamsCacheMilliseconds,
            false
        ) as Observable<boolean>;
    }

    setRememberSearchParams(remember: boolean): Observable<boolean>
    {
        const cacheKey = this.getRememberSearchParamsCacheKey();

        return this.httpClient.post<boolean>(Constants.ApiUrl + 'Profiles/SetRememberSearchParams', remember)
            .pipe(tap(next => this.syncCachedValue<boolean>(cacheKey, remember, next, this.rememberSearchParamsCacheMilliseconds)));
    }

    getVehicleSearchParams(): Observable<VehicleSearchParamsModel>
    {
        const platform = this.getCurrentPlatform();
        const cacheKey = this.getSearchParamsCacheKey('profile-vehicleSearchParams', platform);

        return this.cache.get(
            cacheKey,
            this.httpClient.get<VehicleSearchParamsModel>(Constants.ApiUrl + 'Profiles/VehicleSearchParams/' + platform),
            this.searchParamsCacheMilliseconds,
            false
        ) as Observable<VehicleSearchParamsModel>;
    }

    setVehicleSearchParams(searchParams: VehicleSearchParamsModel): Observable<VehicleSearchParamsModel>
    {
        const platform = this.getCurrentPlatform();
        const cacheKey = this.getSearchParamsCacheKey('profile-vehicleSearchParams', platform);

        return this.httpClient.post<VehicleSearchParamsModel>(Constants.ApiUrl + 'Profiles/VehicleSearchParams/' + platform, searchParams)
            .pipe(tap(next => this.syncCachedValue<VehicleSearchParamsModel>(cacheKey, searchParams, next, this.searchParamsCacheMilliseconds)));
    }

    getContactSearchParams(): Observable<ContactSearchParamsModel>
    {
        const platform = this.getCurrentPlatform();
        const cacheKey = this.getSearchParamsCacheKey('profile-contactSearchParams', platform);

        return this.cache.get(
            cacheKey,
            this.httpClient.get<ContactSearchParamsModel>(Constants.ApiUrl + 'Profiles/ContactSearchParams/' + platform),
            this.searchParamsCacheMilliseconds,
            false
        ) as Observable<ContactSearchParamsModel>;
    }

    setContactSearchParams(searchParams: ContactSearchParamsModel): Observable<ContactSearchParamsModel>
    {
        const platform = this.getCurrentPlatform();
        const cacheKey = this.getSearchParamsCacheKey('profile-contactSearchParams', platform);

        return this.httpClient.post<ContactSearchParamsModel>(Constants.ApiUrl + 'Profiles/ContactSearchParams/' + platform, searchParams)
            .pipe(tap(next => this.syncCachedValue<ContactSearchParamsModel>(cacheKey, searchParams, next, this.searchParamsCacheMilliseconds)));
    }

    getOpportunitySearchParams(): Observable<OpportunitySearchParamsModel>
    {
        const platform = this.getCurrentPlatform();
        const cacheKey = this.getSearchParamsCacheKey('profile-opportunitySearchParams', platform);

        return this.cache.get(
            cacheKey,
            this.httpClient.get<OpportunitySearchParamsModel>(Constants.ApiUrl + 'Profiles/OpportunitySearchParams/' + platform),
            this.searchParamsCacheMilliseconds,
            false
        ) as Observable<OpportunitySearchParamsModel>;
    }

    setOpportunitySearchParams(searchParams: OpportunitySearchParamsModel): Observable<OpportunitySearchParamsModel>
    {
        const platform = this.getCurrentPlatform();
        const cacheKey = this.getSearchParamsCacheKey('profile-opportunitySearchParams', platform);

        return this.httpClient.post<OpportunitySearchParamsModel>(Constants.ApiUrl + 'Profiles/OpportunitySearchParams/' + platform, searchParams)
            .pipe(tap(next => this.syncCachedValue<OpportunitySearchParamsModel>(cacheKey, searchParams, next, this.searchParamsCacheMilliseconds)));
    }

    getSaleSearchParams(): Observable<SaleSearchParamsModel>
    {
        const platform = this.getCurrentPlatform();
        const cacheKey = this.getSearchParamsCacheKey('profile-saleSearchParams', platform);

        return this.cache.get(
            cacheKey,
            this.httpClient.get<SaleSearchParamsModel>(Constants.ApiUrl + 'Profiles/SaleSearchParams/' + platform),
            this.searchParamsCacheMilliseconds,
            false
        ) as Observable<SaleSearchParamsModel>;
    }

    setSaleSearchParams(searchParams: SaleSearchParamsModel): Observable<SaleSearchParamsModel>
    {
        const platform = this.getCurrentPlatform();
        const cacheKey = this.getSearchParamsCacheKey('profile-saleSearchParams', platform);

        return this.httpClient.post<SaleSearchParamsModel>(Constants.ApiUrl + 'Profiles/SaleSearchParams/' + platform, searchParams)
            .pipe(tap(next => this.syncCachedValue<SaleSearchParamsModel>(cacheKey, searchParams, next, this.searchParamsCacheMilliseconds)));
    }
}
